/**
===================================================
Module: frontend.src.components.Projects.tsx
Description: Projects management page component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Project, CreateProjectRequest } from "../types";
import { apiService } from "../services/api";
import TopBar from "./TopBar";
import CreateProjectModal from "./CreateProjectModal";
import EditProjectModal from "./EditProjectModal";
import "../styles/Projects.css";
import "../styles/AdminNotifications.css";

const Projects: React.FC = () => {
  const { t } = useTranslation('projects');
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // System notifications
  const [sysNotifications, setSysNotifications] = useState<any[]>([]);
  const [notifDetail, setNotifDetail] = useState<any | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
    loadNotifications();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const projectsData = await apiService.getProjects();
      setProjects(projectsData);
    } catch (error) {
      setError(t('error.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await apiService.getNotifications();
      setSysNotifications(data.notifications || []);
    } catch {
      // Silently ignore — notifications are non-critical
    }
  };

  const handleCreateProject = async (projectData: CreateProjectRequest) => {
    try {
      setIsUploading(true);
      await apiService.createProject(projectData);
      setShowCreateForm(false);
      loadProjects();
    } catch (error) {
      setError(t('error.createFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}/sessions`);
  };

  const handleMenuClick = () => {
    setShowMenuDropdown(!showMenuDropdown);
  };

  const handleDeleteProjects = () => {
    setIsSelectionMode(true);
    setShowMenuDropdown(false);
    setSelectedProjects(new Set());
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedProjects(new Set());
  };

  const handleProjectSelect = (projectId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedProjects);
    if (isSelected) {
      newSelected.add(projectId);
    } else {
      newSelected.delete(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.size === 0) return;

    const confirmMessage = t('delete.confirm', { count: selectedProjects.size }) + ' ' + t('delete.cannotUndo');
    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const projectIds = Array.from(selectedProjects);

      for (const projectId of projectIds) {
        await apiService.deleteProject(projectId);
      }


      setSelectedProjects(new Set());
      setIsSelectionMode(false);
      loadProjects();
    } catch (error) {
      setError(t('error.deleteFailed'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Add edit project function
  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowEditModal(true);
  };

  const handleUpdateProject = async (name: string, description: string) => {
    if (!editingProject) return;

    try {
      setIsUpdating(true);
      await apiService.updateProject(editingProject.id, { name, description });

      await loadProjects();


      setError("");
    } catch (error) {
      setError(t('error.updateFailed'));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProject(null);
    setError("");
  };

  const getSortedProjects = () => {
    return [...projects].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  };

  const filteredProjects = getSortedProjects().filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description &&
        project.description.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  if (isLoading) {
    return (
      <div className="projects-container">
        <div className="projects-content">
          <TopBar />
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <div className="projects-content">
        <TopBar />

        <div className="project-info">
          <h1 className="project-title">{t('home')}</h1>

          {/* System Notifications Banner — below Home heading */}
          {sysNotifications.length > 0 && (
            <div className="system-notifications-banner">
              <div className="sys-notif-scroll">
                {sysNotifications.map((n) => (
                  <div
                    key={n.id}
                    className={`sys-notif-item notif-${n.severity}`}
                    onClick={() => setNotifDetail(n)}
                  >
                    <span className="sys-notif-icon">
                      {n.severity === 'critical' ? '🔴' : n.severity === 'warning' ? '🟡' : 'ℹ️'}
                    </span>
                    <div className="sys-notif-body">
                      <div className="sys-notif-title">{n.title}</div>
                      <div className="sys-notif-period">
                        {n.created_at?.slice(0, 10)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="breadcrumb">
            <span className="breadcrumb-link">{t('title')}</span>
          </div>

          <div className="search-section">
            <div className="search-container">
              <div className="search-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="search-input"
                placeholder={t('search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label={t('search.placeholder')}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
            <div className="header-actions">
              <button
                className="add-project-btn"
                onClick={() => setShowCreateForm(true)}
                disabled={isUploading || isSelectionMode}
              >
                <span>+</span>
                {t('createProject')}
              </button>

              {isSelectionMode && selectedProjects.size > 0 && (
                <button
                  className="delete-projects-btn"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? t('delete.deleting')
                    : t('delete.deleteSelected', { count: selectedProjects.size })}
                </button>
              )}

              <div className="menu-container">
                <div className="menu-icon" onClick={handleMenuClick}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                {showMenuDropdown && (
                  <div className="menu-dropdown">
                    <button
                      className="menu-item"
                      onClick={handleDeleteProjects}
                    >
                      {t('delete.button')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            {searchQuery && (
              <div className="search-results-info">
                {t('search.results', { filtered: filteredProjects.length, total: projects.length })}
              </div>
            )}
          </div>
        </div>

        <div className="main-content">
          {error && <div className="error-message">{error}</div>}

          {/* Notification Detail Popup */}
          {notifDetail && (
            <div className="notif-detail-overlay" onClick={() => setNotifDetail(null)}>
              <div className="notif-detail-popup" onClick={(e) => e.stopPropagation()}>
                <div className="notif-detail-header">
                  <h3>{notifDetail.title}</h3>
                  <button className="close-btn" onClick={() => setNotifDetail(null)}>&times;</button>
                </div>
                <div className="notif-detail-content">{notifDetail.content}</div>
                <div className="notif-detail-footer">
                  {notifDetail.created_at?.slice(0, 10)}
                </div>
              </div>
            </div>
          )}



          {isSelectionMode && (
            <div className="selection-mode-header">
              <div className="selection-info">
                <span className="selection-count">
                  {t('selection.selected', { selected: selectedProjects.size, total: projects.length })}
                </span>
              </div>
              <div className="selection-actions">
                <button
                  className="select-all-btn"
                  onClick={() =>
                    setSelectedProjects(
                      new Set(projects.map((project) => project.id)),
                    )
                  }
                >
                  {t('selection.selectAll')}
                </button>
                <button
                  className="deselect-all-btn"
                  onClick={() => setSelectedProjects(new Set())}
                >
                  {t('selection.deselectAll')}
                </button>
                <button
                  className="cancel-selection-btn"
                  onClick={handleCancelSelection}
                >
                  {t('selection.cancel')}
                </button>
              </div>
            </div>
          )}

          <div className="projects-section">
            <div className="projects-list">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={`project-card ${isSelectionMode ? "selection-mode" : ""}`}
                  onClick={() =>
                    !isSelectionMode && handleProjectClick(project.id)
                  }
                >
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      className="project-checkbox"
                      checked={selectedProjects.has(project.id)}
                      onChange={(e) =>
                        handleProjectSelect(project.id, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* Add edit button */}
                  {!isSelectionMode && (
                    <button
                      className="edit-project-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }}
                      title={t('edit.title')}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56285 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}

                  <div className="project-info">
                    <div className="project-header">
                      <h3 className="project-name">{project.name}</h3>
                    </div>

                    <p className="project-description">
                      {project.description}
                    </p>

                    <div className="project-meta">
                      <div className="project-date">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>
                          {(() => {
                            const date = new Date(project.created_at);
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            return `${year}/${month}/${day}`;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProjects.length === 0 && !isLoading && (
              <div className="empty-state">
                {searchQuery
                  ? t('search.noResults', { ns: 'common' })
                  : t('noProjects')}
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateProjectModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={handleCreateProject}
        isUploading={isUploading}
      />

      <EditProjectModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateProject}
        projectName={editingProject?.name || ""}
        projectDescription={editingProject?.description || ""}
        isUpdating={isUpdating}
      />
    </div>
  );
};

export default Projects;